export const CLOCK_ROLLBACK_TOLERANCE_MS = 5 * 60 * 1000;

export type OfflineAccessLeaseState = {
  validatedAtServer: string;
  validUntilServer: string | null;
  receivedAtDevice: string;
  maxObservedAtDevice: string;
  invalidatedAtDevice: string | null;
  invalidationReason: "server_invalid" | "expired" | "clock_rollback" | null;
};

export type OfflineAccessState = {
  isValid: boolean;
  /**
   * `auth_pending`: Firebase is still restoring the signed-in user at start-up
   * and there is no saved sign-in to read a lease for, so whose lease decides
   * is not known yet. It never refuses playback or shows the sheet.
   */
  reason:
    | "valid"
    | "missing"
    | "server_invalid"
    | "expired"
    | "clock_rollback"
    | "auth_pending";
  maxObservedAtDevice?: string;
};

/** A known user's lease that does not cover offline playback. */
export type OfflineAccessRefusal = Exclude<
  OfflineAccessState["reason"],
  "valid" | "auth_pending"
>;

export const isOfflineAccessRefusal = (
  reason: OfflineAccessState["reason"],
): reason is OfflineAccessRefusal =>
  reason !== "valid" && reason !== "auth_pending";

const timestamp = (value: string | null | undefined) => {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

export const evaluateOfflineAccessLease = (
  lease: OfflineAccessLeaseState | null | undefined,
  now = new Date(),
): OfflineAccessState => {
  if (!lease) return { isValid: false, reason: "missing" };
  if (lease.invalidationReason) {
    return { isValid: false, reason: lease.invalidationReason };
  }

  const validatedAtServer = timestamp(lease.validatedAtServer);
  const validUntilServer = timestamp(lease.validUntilServer);
  const receivedAtDevice = timestamp(lease.receivedAtDevice);
  const maxObservedAtDevice = timestamp(lease.maxObservedAtDevice);
  if (
    validatedAtServer === null ||
    validUntilServer === null ||
    receivedAtDevice === null ||
    maxObservedAtDevice === null ||
    validUntilServer <= validatedAtServer
  ) {
    return { isValid: false, reason: "server_invalid" };
  }

  const nowMs = now.getTime();
  if (nowMs < maxObservedAtDevice - CLOCK_ROLLBACK_TOLERANCE_MS) {
    return { isValid: false, reason: "clock_rollback" };
  }

  // Measure the server-issued duration using elapsed device time. This avoids
  // trusting the device's absolute clock while still working without network.
  const effectiveNow = Math.max(nowMs, maxObservedAtDevice);
  const leaseDuration = validUntilServer - validatedAtServer;
  if (effectiveNow - receivedAtDevice >= leaseDuration) {
    return { isValid: false, reason: "expired" };
  }

  return {
    isValid: true,
    reason: "valid",
    maxObservedAtDevice: new Date(effectiveNow).toISOString(),
  };
};
